import { cn } from '@heidi/utils';

import { PANEL_WIDTH } from './panel.types';

// ============================================================================
// Floating Panel Styles
// ============================================================================

/**
 * Base styles for floating panel content (NavigationMenuContent)
 * Styled to match sidebar aesthetic with subtle elevation.
 */
export const panelFloatingContentClasses = cn(
  // Width constraint - prevent content overflow
  'overflow-hidden',
  // Match sidebar styling
  'z-20 rounded-lg bg-surface-accent text-text-secondary',
  'border shadow-lg'
  // Note: Animations are handled by SidebarNavigationMenuContent (vertical motion)
);

// ============================================================================
// Pinned Panel Styles
// ============================================================================

/**
 * Base styles for pinned panel wrapper (creates space in flex flow like sidebar gap)
 */
export const panelPinnedWrapperClasses = cn(
  'hidden sm:block',
  'h-screen shrink-0',
  'transition-[width] duration-200 ease-out motion-reduce:transition-none'
);

/**
 * Base styles for pinned panel container (the visible sidebar-like panel)
 */
export const panelPinnedClasses = cn(
  // Match sidebar styling
  'size-full bg-surface-accent text-text-secondary',
  'border-e',
  'flex flex-col',
  // CRITICAL: overflow-hidden creates the clipping boundary for content truncation
  'overflow-hidden'
);

/**
 * Pinned panel container for scroll area.
 *
 * IMPORTANT: Width constraint fix for Radix ScrollArea
 * ====================================================
 * See panelFloatingContainerClasses for detailed explanation.
 * The same fix is applied via viewportClassName in panel-pinned.tsx:
 * - `[&>div]:!block` - Forces internal div from display:table to display:block
 * - `[&>div]:!w-full` - Forces width to 100% of container
 */
export const panelPinnedContainerClasses = cn('flex-1 overflow-auto');

// ============================================================================
// Mobile Panel Styles
// ============================================================================

/**
 * Mobile panel container using stacked absolute views.
 * Each view is full-size and slides in/out independently.
 * - overflow-hidden: clips views during animation
 * - touch-pan-y: prevents horizontal touch/swipe gestures
 */
export const panelMobileContainerClasses = cn(
  'relative size-full',
  'overflow-hidden',
  'touch-pan-y'
);

/**
 * Mobile view panel - absolutely positioned full-size view.
 * Uses inset-0 for guaranteed full coverage regardless of parent chain.
 * - absolute inset-0: fills container completely
 * - Animation handled by motion/react for iOS-like spring physics
 * - overflow-y-auto: vertical scrolling for content
 */
export const panelMobileViewClasses = cn(
  'absolute inset-0 flex flex-col',
  'overflow-y-auto'
);

/**
 * Panel header container - matches sidebar header height for visual alignment.
 * Uses CSS variable --sidebar-header-height for consistency with PageShell.Header.
 */
export const panelHeaderClasses = cn(
  'flex h-[var(--sidebar-header-height)] shrink-0 items-center gap-2 border-b px-3'
);

/**
 * Mobile panel back button container.
 * Height matches sidebar header for visual consistency.
 */
export const panelMobileBackButtonClasses = cn(
  'flex h-12 shrink-0 items-center gap-2 border-b px-4'
);

// ============================================================================
// CSS Variable Helpers
// ============================================================================

/**
 * Get combined floating panel styles (width only, maxHeight is on ScrollArea)
 */
export const getPanelFloatingStyles = (): React.CSSProperties => ({
  width: PANEL_WIDTH,
});

// ============================================================================
// Panel Styles
// ============================================================================

/**
 * Base styles shared by both pinned and floating panel modes.
 * Provides consistent visual appearance for secondary panels.
 * @see https://www.figma.com/design/s7idxuz906cbzg10o8vmiY/Visioning?node-id=211-46085
 */
export const panelBaseClasses = cn(
  // Layout - flex column
  'flex flex-col',
  // Prevent content overflow
  'overflow-hidden',
  // Horizontal padding only - header controls its own vertical spacing
  'px-2',
  // Visual styling - matches Figma SecondaryMenu
  'bg-surface-accent'
);

/**
 * Pinned mode styles - panel renders in document flow.
 * Pushes main content aside when visible.
 */
export const panelPinnedModeClasses = cn(
  panelBaseClasses,
  // Sizing - full height, fixed width (via style prop)
  'h-full shrink-0'
);

/**
 * Floating mode styles - panel renders with fixed positioning.
 * Hovers over content without affecting layout.
 */
export const panelFloatingModeClasses = cn(
  panelBaseClasses,
  // Positioning
  'fixed top-0 z-panel h-dvh',
  // Elevation - clip left shadow since panel is adjacent to sidebar
  'shadow-lg [clip-path:inset(0_-20px_0_0)]',
  // Animation: opacity fade
  'transition-opacity duration-200 ease-out',
  // Disable transitions when switching content
  'data-[switching=true]:transition-none',
  // Closed state
  'data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0',
  // Open state
  'data-[state=open]:opacity-100'
);
